import { AfterViewInit, Component, inject, Injector, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import {
  FIELD_ASYNC_PRECONDITIONS_INJECTION,
  FIELD_CONFIG_INJECTION,
  FIELD_ENTITY_INJECTION,
  FIELD_EXTRA_DATA,
  FIELD_PRECONDITIONS_INJECTION
} from '@app/core/model/other/field-config';
import { AbstractFieldGroupBuilder, FieldStateMode } from '@app/shared/components/fields/abstract.field';
import { FieldAnchorDirective } from '@app/shared/components/fields/field-anchor.directive';
import { getFieldBuilder } from '@app/shared/components/fields/field-builder-selector';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Component({
  selector: 'single-field-builder',
  templateUrl: './single-field-builder.component.html',
  standalone: false
})
export class SingleFieldBuilderComponent extends AbstractFieldGroupBuilder implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;

  private injector = inject(Injector);

  // Static parameter in Angular 8
  @ViewChild(FieldAnchorDirective, {static: true}) public fieldHost: FieldAnchorDirective;

  public ngOnInit(): void {
    this.setFieldGroupInfo([], {
      state: new ReplaySubject<FieldStateMode>(1),
      isSingleField: true,
      isDisplayed: new BehaviorSubject<boolean>(false),
      value: {},
      initialValue: {},
      hook: {}
    });
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    const fieldGroupContainerRef = this.fieldHost.viewContainerRef;
    fieldGroupContainerRef.clear();

    // Subscribe to any change in state of the single
    this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newMode) => {
      this.currentMode = newMode;
    });

    // Cannot edit this field's group if the user has no write access to some fields in this group
    this.preconditionsForEdition &&= this.fieldGroup.children.every(fieldConfig =>
      this.accessManager.hasFieldWriteAccess(fieldConfig.field.alias, fieldConfig.fieldEntityType)
    );

    this.fieldGroup.children
      .filter(fieldConfig => {
        // Remove fields not related to the current asset type
        const fieldAssetTypesIds: string[] = fieldConfig.field.validators
          .firstOrNull(v => v.type === ValidatorType.FOR_ASSET_TYPES)
          ?.definition?.split(',');

        return !fieldAssetTypesIds || (
          isAssetRelatedEntity(this.entity)
          && this.entity.relatedAssets.some(({typeId}) => fieldAssetTypesIds.includes(typeId))
        );
      })
      .forEach((fieldConfig, index) => {
        // get the field type to create
        const fieldComponent = getFieldBuilder(fieldConfig);

        if (!fieldConfig.field.computed) {
        // If at least one member of the group is not computed, the group must be displayed
        this.getFieldGroupInfo(['isDisplayed']).next(true);
      }

      // Create the corresponding component if it is recognized
      if (fieldComponent !== null) {
        const fieldRef = fieldGroupContainerRef.createComponent(fieldComponent.fieldBuilder, {
          index: index,
          injector: Injector.create({
            parent: this.injector,
            providers: [
              {provide: FIELD_CONFIG_INJECTION, useValue: fieldConfig},
              {provide: FIELD_ENTITY_INJECTION, useValue: this.entity},
              {
                provide: FIELD_PRECONDITIONS_INJECTION,
                useValue: this.preconditionsForEdition && this.validationService.isFieldEditable(
                  fieldConfig,
                  this.entity
                )
              },
              {
                provide: FIELD_ASYNC_PRECONDITIONS_INJECTION,
                useValue: this.validationService.validateAsyncConditions(this.entity, fieldConfig.conditionsToEdit)
              },
              {
                provide: FIELD_EXTRA_DATA, useValue: {
                  isSingleField: true,
                  fieldGroupCode: this.fieldGroup.code,
                  propertiesPath: this.data.propertiesPath,
                  focusable: fieldComponent.focusable,
                  formId: this.data.formId
                }
              }
            ]
          })
        });
        fieldRef.changeDetectorRef.detectChanges();

        this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newMode) => {
          if (newMode === FieldStateMode.EDIT) fieldRef.instance.focus();
        });

      } else {
        console.warn('Le type de champ "' + fieldConfig.field?.fieldType + '" n\'est pas reconnu');
      }

    });
  }
}
